import { articleCard } from '~/schemas/common'

/**
 * Standard article scoring configuration for priority content
 */
const ARTICLE_SCORING_BOOSTS = {
  'sellers-guide': 999999,
  'buyers-guide': 999998,
  'buyer-guide': 999997,
  'investment-guide': 999996,
  'record-prices': 999995,
  'most-famous': 999994,
  'auction-watch': 1000,
  'market-editor-report': 999,
} as const

/**
 * Builds standardized scoring logic for articles
 */
export const buildArticleScoring = (
  customBoosts: Record<string, number> = {}
): string => {
  const allBoosts = { ...ARTICLE_SCORING_BOOSTS, ...customBoosts }

  const boostStrings = Object.entries(allBoosts).map(
    ([pattern, boost]) => `boost(slug.current match "**${pattern}**", ${boost})`
  )

  return boostStrings.join(',\n        ')
}

/**
 * Builds inverse query for translated articles with scoring and filtering
 * This is the main utility for complex article queries that need translation support
 */
export const buildInverseTranslationQuery = (
  filters: string,
  lang = 'en',
  fieldName = 'articles',
  orderBy = 'coalesce(publishDate, _createdAt) desc',
  limit = 8,
  customScoring: Record<string, number> = {}
): string => {
  const scoring = buildArticleScoring(customScoring)

  if (lang === 'en') {
    return `"${fieldName}": *[_type == "article" && ${filters} && (!defined(language) || language == $lang)]
      | score(${scoring})
      | order(_score desc, ${orderBy})
      [0...${limit}] {
        ${articleCard}
      }`
  }

  return `"${fieldName}": *[_type == "article" && 
      ${filters} &&
      _id in *[_type == "translation.metadata"].translations[_key == $lang].value._ref
    ]
    | score(${scoring})
    | order(_score desc, ${orderBy})
    [0...${limit}] {
      "article": *[_type == "translation.metadata" && references(^._id)][0].translations[_key == $lang].value-> {
        ${articleCard}
      }
    }`
}

/**
 * Builds GROQ query fragments for fetching translated articles
 * Uses different strategies for English vs other languages
 */
export const buildTranslatedArticlesQuery = (
  baseQuery: string,
  lang = 'en',
  fieldName = 'relatedArticles'
): string => {
  // For English, get articles directly
  if (lang === 'en') {
    return `"${fieldName}": ${baseQuery}-> {
      ${articleCard}
    }`
  }

  // For other languages, get translations via metadata
  return `"${fieldName}": ${baseQuery}->{
    "article": *[
      _type == "translation.metadata" && references(^._id)
    ][0].translations[_key == $lang].value->{
      ${articleCard}
    }
  }`
}

/**
 * Builds GROQ query for direct article queries with language filtering
 * Handles both English and translated content appropriately
 */
export const buildArticleQueryWithLanguage = (
  baseFilters: string,
  lang = 'en',
  orderBy = 'coalesce(publishDate, _createdAt) desc'
): string => {
  // For English, query articles directly with language filter
  if (lang === 'en') {
    return `*[_type == "article" && (!defined(language) || language == $lang) ${baseFilters}] | order(${orderBy}) {
      ${articleCard}
    }`
  }

  // For other languages, query translation metadata
  return `*[_type == "translation.metadata" && count(translations[_key == $lang && defined(value)]) > 0] {
    "article": translations[_key == $lang].value->{
      ${articleCard}
    }
  }[defined(article) && article._type == "article" ${baseFilters.replace(/\^/g, 'article')}] | order(${orderBy.replace(/\^/g, 'article')})`
}

/**
 * Builds related articles query with language-aware translation handling
 * Combines curated articles with automated suggestions
 */
export const buildRelatedArticlesWithTranslations = (
  curatedQuery: string,
  automatedQuery: string,
  lang = 'en',
  limit = 8
): string => {
  if (lang === 'en') {
    return `"relatedArticles": (
      coalesce(
        ${curatedQuery}-> {
          ${articleCard}
        },
        []
      ) + ${automatedQuery} {
        ${articleCard}
      }
    )[0...${limit}]`
  }

  return `"relatedArticles": (
    coalesce(
      ${curatedQuery}->{
        "article": *[
          _type == "translation.metadata" && references(^._id)
        ][0].translations[_key == $lang].value->{
          ${articleCard}
        }
      },
      []
    ) + ${automatedQuery.replace('*[', '*[_type == "translation.metadata" && count(translations[_key == $lang && defined(value)]) > 0] { "article": translations[_key == $lang].value-> } [defined(article) && article._type == "article" &&')}{
      "article": translations[_key == $lang].value->{
        ${articleCard}
      }
    }
  )[0...${limit}]`
}

/**
 * Builds GROQ query for resolving reference arrays to their translated documents
 * Only returns documents that have translations in the specified language (no fallback)
 * Designed for simple reference arrays like homepageArticles, featuredArtworks, etc.
 *
 * @param fieldName - The name of the field containing the array of references
 * @param lang - The language code to resolve translations for
 * @param projection - The GROQ projection to apply to each translated document
 * @returns GROQ query string for the translated references
 */
export const buildTranslatedReferenceArray = (
  fieldName: string,
  lang: string,
  projection: string
): string => {
  // For English, just return the standard reference resolution
  if (lang === 'en') {
    return `"${fieldName}": ${fieldName}[]-> {
      ${projection}
    }`
  }

  // For other languages, filter to only include references with translations
  // and resolve directly to the translated documents
  return `"${fieldName}": ${fieldName}[]{
    "original": @,
    "translated": *[_type == "translation.metadata" && references(^._ref)][0].translations[_key=="${lang}"][0].value
  }[defined(translated)].translated->{
    ${projection}
  }`
}

/**
 * Helper to add language filtering to existing GROQ queries
 * Adds appropriate language constraints while preserving existing filters
 */
export const addLanguageFiltering = (existingFilter: string): string => {
  const languageFilter = `(!defined(language) || language == $lang)`

  if (!existingFilter.trim()) {
    return ` && ${languageFilter}`
  }

  return `${existingFilter} && ${languageFilter}`
}

/**
 * Standardized language parameter handling for Sanity queries
 */
export const getLanguageParams = (lang?: string) => ({
  lang: lang || 'en',
})

/**
 * Post-processes article query results to extract translated articles from nested structure
 *
 * When querying translated content, GROQ returns a nested structure like:
 * [{article: {...}}, {article: {...}}] for non-English languages
 *
 * This function flattens the structure to: [{...}, {...}]
 *
 * Required because GROQ cannot flatten complex dereferencing chains at query-level.
 *
 * @param articles - Array of objects potentially containing nested article data
 * @param lang - Language code (returns early for 'en' as English queries are already flat)
 * @returns Flat array of article objects with duplicates removed
 *
 * @example
 * // French query returns: [{article: {_id: '1', title: 'Foo'}}, {article: {_id: '2', title: 'Bar'}}]
 * const articles = processInverseQueryResults(data.articles, 'fr')
 * // Returns: [{_id: '1', title: 'Foo'}, {_id: '2', title: 'Bar'}]
 */
export const processInverseQueryResults = <T extends { article?: any }>(
  articles: T[],
  lang: string
): any[] => {
  if (lang === 'en' || !articles) {
    return articles || []
  }

  return articles
    .map(item => item.article)
    .filter(article => article !== null && article !== undefined)
    .flat() // Flatten arrays since translations can come wrapped in arrays
    .filter(
      (article, index, self) =>
        // Remove duplicates based on _id
        index === self.findIndex(a => a._id === article._id)
    )
}

/**
 * Builds count query for articles with language filtering
 */
export const buildArticleCountWithLanguage = (
  baseFilters: string,
  lang = 'en'
): string => {
  if (lang === 'en') {
    return `count(*[_type == "article" && (!defined(language) || language == $lang) ${baseFilters}])`
  }

  return `count(*[_type == "translation.metadata" && count(translations[_key == $lang && defined(value)]) > 0] {
    "article": translations[_key == $lang].value
  }[defined(article) && article._type == "article" ${baseFilters.replace(/\^/g, 'article')}])`
}
